import { useMemo } from 'react';
import { BarChart3, Download, HelpCircle, Loader2 } from 'lucide-react';
import { formatPrice } from '../../../../utils/priceHelpers';
import { HelpTooltip } from '../../../../Components/HelpTooltip';
import { VendorFinancialCompiledData } from './useFinancialCompiled';

interface SyntheseViewProps {
  data: VendorFinancialCompiledData;
  isTransferPrice?: boolean;
  onExport?: () => void;
  isExporting?: boolean;
  retroDelayMonths?: number;
  onRetroDelayChange?: (value: number) => void;
}

export function SyntheseView({ data, isTransferPrice = false, onExport, isExporting = false, retroDelayMonths = 0, onRetroDelayChange }: SyntheseViewProps) {
  const hasStripePayments = (data.fraisStripe?.total?.count || 0) > 0;

  // Quand isTransferPrice est désactivé, la rétrocession ne doit pas être considérée
  // comme une charge : on la neutralise dans "Total charges" et on la ré-ajoute au
  // bilan (qui est calculé côté API en la déduisant déjà des revenus).
  const effectiveRetrocession = useMemo(() => {
    if (isTransferPrice) return data.retrocession.total;
    return {
      count: 0,
      percentage: 0,
      factureTtc: 0,
      factureHt: 0,
      encaisseTtc: 0,
      encaisseHt: 0,
      aEncaisserTtc: 0,
      aEncaisserHt: 0,
    };
  }, [isTransferPrice, data.retrocession.total]);

  const bilanDisplay = useMemo(() => {
    if (isTransferPrice) return data.bilan;
    return {
      ...data.bilan,
      factureTtc: (data.bilan.factureTtc || 0) + (data.retrocession.total.factureTtc || 0),
      factureHt: (data.bilan.factureHt || 0) + (data.retrocession.total.factureHt || 0),
      encaisseTtc: (data.bilan.encaisseTtc || 0) + (data.retrocession.total.encaisseTtc || 0),
      encaisseHt: (data.bilan.encaisseHt || 0) + (data.retrocession.total.encaisseHt || 0),
      aEncaisserTtc: (data.bilan.aEncaisserTtc || 0) + (data.retrocession.total.aEncaisserTtc || 0),
      aEncaisserHt: (data.bilan.aEncaisserHt || 0) + (data.retrocession.total.aEncaisserHt || 0),
    };
  }, [isTransferPrice, data.bilan, data.retrocession.total]);

  return (
    <div>
      {/* Bandeau titre */}
      <div className="tw-flex tw-items-center tw-justify-between tw-bg-affinicar-primary tw-text-white tw-uppercase tw-font-bold tw-px-4 tw-py-4 tw-rounded-lg tw-mb-4 tw-min-h-[56px]">
        <div className="tw-flex tw-items-center tw-gap-2">
          <BarChart3 className="tw-w-4 sm:tw-w-5 tw-h-4 sm:tw-h-5 tw-shrink-0" />
          <span className="tw-text-sm sm:tw-text-base tw-font-bold tw-leading-none">Synthèse financière</span>
        </div>
        {onExport && (
          <button
            onClick={onExport}
            disabled={isExporting}
            className="tw-flex tw-items-center tw-gap-1.5 tw-bg-white/20 hover:tw-bg-white/30 tw-text-white tw-text-sm tw-font-semibold tw-px-3 tw-py-1.5 tw-rounded-md tw-transition-colors tw-normal-case disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
          >
            {isExporting ? <Loader2 size={16} className="tw-animate-spin" /> : <Download size={16} />}
            <span className="tw-hidden sm:tw-inline">{isExporting ? 'Export…' : 'Export'}</span>
          </button>
        )}
      </div>

      {/* Filtre délai rétrocession */}
      {onRetroDelayChange && isTransferPrice && (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-3 tw-mb-4">
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <span className="tw-text-sm tw-text-gray-600">Délai de rétrocession</span>
            <span className="tw-group tw-relative">
              <HelpCircle size={14} className="tw-text-gray-400 tw-cursor-help" />
              <span className="tw-absolute tw-left-1/2 tw--translate-x-1/2 tw-bottom-full tw-mb-2 tw-px-3 tw-py-2 tw-bg-gray-900 tw-text-white tw-text-xs tw-rounded-lg tw-whitespace-nowrap tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-pointer-events-none tw-z-50">
                Nombre de mois entre la création du contrat et le versement de la rétrocession
              </span>
            </span>
            <input
              type="number"
              min="0"
              max="24"
              value={retroDelayMonths}
              onChange={(e) => onRetroDelayChange(parseInt(e.target.value) || 0)}
              className="tw-w-12 sm:tw-w-14 tw-px-2 tw-py-1.5 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm tw-text-center focus:tw-ring-affinicar-primary focus:tw-border-affinicar-primary"
            />
            <span className="tw-text-sm tw-text-gray-500">mois</span>
          </div>
        </div>
      )}

      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-overflow-x-auto tw-mb-6">
      <table className="tw-w-full tw-text-xs sm:tw-text-sm tw-min-w-[900px]">
        <thead>
          <tr className="tw-border-b tw-border-gray-200">
            <th className="tw-px-4 tw-py-2 tw-text-left tw-font-semibold tw-text-gray-700" colSpan={3}></th>
            <th className="tw-px-3 tw-py-2 tw-text-center tw-font-semibold tw-text-gray-600 tw-bg-gray-100" colSpan={2}>
              FACTURATION
            </th>
            <th className="tw-px-3 tw-py-2 tw-text-center tw-font-semibold tw-text-emerald-700 tw-bg-emerald-50" colSpan={2}>
              ENCAISS&Eacute; / D&Eacute;CAISS&Eacute;
            </th>
            <th className="tw-px-3 tw-py-2 tw-text-center tw-font-semibold tw-text-amber-700 tw-bg-amber-50" colSpan={2}>
              &Agrave; ENCAISSER / D&Eacute;CAISSER
            </th>
          </tr>
          <tr className="tw-border-b tw-border-gray-300 tw-text-xs tw-uppercase">
            <th className="tw-px-2 sm:tw-px-4 tw-py-2 tw-text-left tw-text-gray-500 tw-min-w-[160px] sm:tw-w-64 tw-sticky tw-left-0 tw-bg-white tw-z-10"></th>
            <th className="tw-px-2 tw-py-2 tw-text-right tw-text-gray-500 tw-w-16">Nb</th>
            <th className="tw-px-2 tw-py-2 tw-text-right tw-text-gray-500 tw-w-16">%</th>
            <th className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500 tw-bg-gray-100/50">TTC</th>
            <th className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500 tw-bg-gray-100/50">HT</th>
            <th className="tw-px-3 tw-py-2 tw-text-right tw-text-emerald-600 tw-bg-emerald-50/50">TTC</th>
            <th className="tw-px-3 tw-py-2 tw-text-right tw-text-emerald-600 tw-bg-emerald-50/50">HT</th>
            <th className="tw-px-3 tw-py-2 tw-text-right tw-text-amber-600 tw-bg-amber-50/50">TTC</th>
            <th className="tw-px-3 tw-py-2 tw-text-right tw-text-amber-600 tw-bg-amber-50/50">HT</th>
          </tr>
        </thead>
        <tbody>
          {/* ===== REVENUS DU PROGRAMME ===== */}
          <tr className="tw-bg-affinicar-lighter tw-border-t-2 tw-border-affinicar-primary/30">
            <td colSpan={9} className="tw-px-4 tw-py-3 tw-font-bold tw-text-affinicar-primary tw-text-base">
              Revenus du programme
            </td>
          </tr>

          {/* Vente contrats de garantie */}
          <tr className="tw-border-b tw-border-gray-100 tw-bg-affinicar-lighter/30">
            <td className="tw-px-4 tw-py-2 tw-font-semibold tw-text-affinicar-primary">
              Vente contrats de garantie
              <HelpTooltip
                content="Facturation des garanties au client final (au comptant ou mensualis&eacute;). Un contrat annul&eacute; compte pour ses encaissements, sans entrer dans le nombre de contrats."
                id="tooltip-vente-contrats"
              />
            </td>
            <td className="tw-px-2 tw-py-2 tw-text-right tw-font-semibold">
              {data.venteContrats.total.count || 0}
            </td>
            <td className="tw-px-2 tw-py-2 tw-text-right"></td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium">
              {formatPrice(data.venteContrats.total.factureTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500">
              {formatPrice(data.venteContrats.total.factureHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-bg-emerald-50/30">
              {formatPrice(data.venteContrats.total.encaisseTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500 tw-bg-emerald-50/30">
              {formatPrice(data.venteContrats.total.encaisseHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-bg-amber-50/30">
              {formatPrice(data.venteContrats.total.aEncaisserTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500 tw-bg-amber-50/30">
              {formatPrice(data.venteContrats.total.aEncaisserHt || 0)}
            </td>
          </tr>
          {/* Sous-lignes ventil&eacute;es */}
          <tr className="tw-border-b tw-border-gray-100">
            <td className="tw-px-4 tw-py-1.5 tw-pl-10 tw-text-gray-600">
              Inclusion
              <HelpTooltip
                content="Garanties offertes au client final, aucune facturation"
                id="tooltip-inclusion"
              />
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-600">
              {data.venteContrats.inclusion.count || 0}
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-500">
              {data.venteContrats.inclusion.percentage || 0}%
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500">
              {formatPrice(data.venteContrats.inclusion.factureTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400">
              {formatPrice(data.venteContrats.inclusion.factureHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500 tw-bg-emerald-50/20">
              {formatPrice(data.venteContrats.inclusion.encaisseTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-emerald-50/20">
              {formatPrice(data.venteContrats.inclusion.encaisseHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-600 tw-bg-amber-50/20">
              {formatPrice(data.venteContrats.inclusion.aEncaisserTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-amber-50/20">
              {formatPrice(data.venteContrats.inclusion.aEncaisserHt || 0)}
            </td>
          </tr>
          <tr className="tw-border-b tw-border-gray-100">
            <td className="tw-px-4 tw-py-1.5 tw-pl-10 tw-text-gray-600">
              Comptant
              <HelpTooltip
                content="Garanties factur&eacute;es au client final, facturation et encaissement au comptant"
                id="tooltip-comptant"
              />
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-600">
              {(data.venteContrats.comptant.count || 0) + (data.venteContrats.comptantAgence?.count || 0)}
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-500">
              {(
                parseFloat(String(data.venteContrats.comptant.percentage || 0)) +
                parseFloat(String(data.venteContrats.comptantAgence?.percentage || 0))
              ).toFixed(1)}%
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500">
              {formatPrice((data.venteContrats.comptant.factureTtc || 0) + (data.venteContrats.comptantAgence?.factureTtc || 0))}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400">
              {formatPrice((data.venteContrats.comptant.factureHt || 0) + (data.venteContrats.comptantAgence?.factureHt || 0))}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500 tw-bg-emerald-50/20">
              {formatPrice((data.venteContrats.comptant.encaisseTtc || 0) + (data.venteContrats.comptantAgence?.encaisseTtc || 0))}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-emerald-50/20">
              {formatPrice((data.venteContrats.comptant.encaisseHt || 0) + (data.venteContrats.comptantAgence?.encaisseHt || 0))}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-600 tw-bg-amber-50/20">
              {formatPrice((data.venteContrats.comptant.aEncaisserTtc || 0) + (data.venteContrats.comptantAgence?.aEncaisserTtc || 0))}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-amber-50/20">
              {formatPrice((data.venteContrats.comptant.aEncaisserHt || 0) + (data.venteContrats.comptantAgence?.aEncaisserHt || 0))}
            </td>
          </tr>
          {hasStripePayments && (
          <tr className="tw-border-b tw-border-gray-100">
            <td className="tw-px-4 tw-py-1.5 tw-pl-10 tw-text-gray-600">
              Mensualis&eacute;
              <HelpTooltip
                content="Garanties factur&eacute;es au client final, facturation et encaissement par mensualit&eacute;s"
                id="tooltip-mensualise"
              />
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-600">
              {data.venteContrats.mensualise.count || 0}
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-500">
              {data.venteContrats.mensualise.percentage || 0}%
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500">
              {formatPrice(data.venteContrats.mensualise.factureTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400">
              {formatPrice(data.venteContrats.mensualise.factureHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500 tw-bg-emerald-50/20">
              {formatPrice(data.venteContrats.mensualise.encaisseTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-emerald-50/20">
              {formatPrice(data.venteContrats.mensualise.encaisseHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-600 tw-bg-amber-50/20">
              {formatPrice(data.venteContrats.mensualise.aEncaisserTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-amber-50/20">
              {formatPrice(data.venteContrats.mensualise.aEncaisserHt || 0)}
            </td>
          </tr>
          )}

          {isTransferPrice && (<>
          {/* Refacturation au R&eacute;seau */}
          <tr className="tw-border-b tw-border-gray-100 tw-bg-affinicar-lighter/30">
            <td className="tw-px-4 tw-py-2 tw-font-semibold tw-text-affinicar-primary">
              Refacturation au R&eacute;seau
              <HelpTooltip
                content="Facturation des garanties aux agences, consid&eacute;r&eacute;e comme encaiss&eacute; imm&eacute;diatement dans ce tableau (M+1 en r&eacute;alit&eacute;)"
                id="tooltip-refacturation"
              />
            </td>
            <td className="tw-px-2 tw-py-2 tw-text-right tw-font-semibold">
              {data.refacturation.total.count || 0}
            </td>
            <td className="tw-px-2 tw-py-2 tw-text-right"></td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium">
              {formatPrice(data.refacturation.total.factureTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500">
              {formatPrice(data.refacturation.total.factureHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-bg-emerald-50/30">
              {formatPrice(data.refacturation.total.encaisseTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500 tw-bg-emerald-50/30">
              {formatPrice(data.refacturation.total.encaisseHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-bg-amber-50/30">
              {formatPrice(data.refacturation.total.aEncaisserTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500 tw-bg-amber-50/30">
              {formatPrice(data.refacturation.total.aEncaisserHt || 0)}
            </td>
          </tr>
          {/* Sous-ligne: Inclusions pures */}
          <tr className="tw-border-b tw-border-gray-100">
            <td className="tw-px-4 tw-py-1.5 tw-pl-10 tw-text-gray-600">
              Garanties incluses
              <HelpTooltip
                content="Garanties dont le prix de base est 0, refactur&eacute; syst&eacute;matiquement au R&eacute;seau"
                id="tooltip-garanties-incluses"
              />
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-600">
              {data.refacturation.garantiesIncluses.count || 0}
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-500"></td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500">
              {formatPrice(data.refacturation.garantiesIncluses.factureTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400">
              {formatPrice(data.refacturation.garantiesIncluses.factureHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500 tw-bg-emerald-50/20">
              {formatPrice(data.refacturation.garantiesIncluses.encaisseTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-emerald-50/20">
              {formatPrice(data.refacturation.garantiesIncluses.encaisseHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-600 tw-bg-amber-50/20">
              {formatPrice(data.refacturation.garantiesIncluses.aEncaisserTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-amber-50/20">
              {formatPrice(data.refacturation.garantiesIncluses.aEncaisserHt || 0)}
            </td>
          </tr>
          {/* Sous-ligne: Garanties payantes offertes */}
          <tr className="tw-border-b tw-border-gray-100">
            <td className="tw-px-4 tw-py-1.5 tw-pl-10 tw-text-gray-600">
              Garanties payantes offertes
              <HelpTooltip
                content="Garanties dont le prix de base est > &agrave; 0 et vente &agrave; z&eacute;ro, refactur&eacute; au r&eacute;seau"
                id="tooltip-garanties-offertes"
              />
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-600">
              {data.refacturation.garantiesPayantesOffertes?.count || 0}
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-500"></td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500">
              {formatPrice(data.refacturation.garantiesPayantesOffertes?.factureTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400">
              {formatPrice(data.refacturation.garantiesPayantesOffertes?.factureHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500 tw-bg-emerald-50/20">
              {formatPrice(data.refacturation.garantiesPayantesOffertes?.encaisseTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-emerald-50/20">
              {formatPrice(data.refacturation.garantiesPayantesOffertes?.encaisseHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-600 tw-bg-amber-50/20">
              {formatPrice(data.refacturation.garantiesPayantesOffertes?.aEncaisserTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-amber-50/20">
              {formatPrice(data.refacturation.garantiesPayantesOffertes?.aEncaisserHt || 0)}
            </td>
          </tr>
          {/* Sous-ligne: Garanties vendues avec d&eacute;cote */}
          <tr className="tw-border-b tw-border-gray-100">
            <td className="tw-px-4 tw-py-1.5 tw-pl-10 tw-text-gray-600">
              Garanties vendues avec d&eacute;cote
              <HelpTooltip
                content="Garanties dont le prix de base est > &agrave; 0, refactur&eacute; au r&eacute;seau si vendues moins ch&egrave;res ou offertes"
                id="tooltip-garanties-decote"
              />
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-600">
              {data.refacturation.garantiesPayantesAvecDecote.count || 0}
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-500"></td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500">
              {formatPrice(data.refacturation.garantiesPayantesAvecDecote.factureTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400">
              {formatPrice(data.refacturation.garantiesPayantesAvecDecote.factureHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500 tw-bg-emerald-50/20">
              {formatPrice(data.refacturation.garantiesPayantesAvecDecote.encaisseTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-emerald-50/20">
              {formatPrice(data.refacturation.garantiesPayantesAvecDecote.encaisseHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-600 tw-bg-amber-50/20">
              {formatPrice(data.refacturation.garantiesPayantesAvecDecote.aEncaisserTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-amber-50/20">
              {formatPrice(data.refacturation.garantiesPayantesAvecDecote.aEncaisserHt || 0)}
            </td>
          </tr>
          </>)}

          {/* ===== TOTAL REVENUS ===== */}
          <tr className="tw-bg-affinicar-lighter tw-border-t tw-border-affinicar-primary/40">
            <td className="tw-px-4 tw-py-2 tw-font-bold tw-text-affinicar-primary">
              Total revenus
            </td>
            <td className="tw-px-2 tw-py-2 tw-text-right"></td>
            <td className="tw-px-2 tw-py-2 tw-text-right"></td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-bold tw-text-green-800">
              {formatPrice(
                (data.venteContrats.total.factureTtc || 0) +
                (data.refacturation.total.factureTtc || 0)
              )}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-text-green-700">
              {formatPrice(
                (data.venteContrats.total.factureHt || 0) +
                (data.refacturation.total.factureHt || 0)
              )}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-bold tw-text-green-800 tw-bg-emerald-100">
              {formatPrice(
                (data.venteContrats.total.encaisseTtc || 0) +
                (data.refacturation.total.encaisseTtc || 0)
              )}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-text-green-700 tw-bg-emerald-100">
              {formatPrice(
                (data.venteContrats.total.encaisseHt || 0) +
                (data.refacturation.total.encaisseHt || 0)
              )}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-bold tw-text-green-800 tw-bg-amber-100">
              {formatPrice(
                (data.venteContrats.total.aEncaisserTtc || 0) +
                (data.refacturation.total.aEncaisserTtc || 0)
              )}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-text-green-700 tw-bg-amber-100">
              {formatPrice(
                (data.venteContrats.total.aEncaisserHt || 0) +
                (data.refacturation.total.aEncaisserHt || 0)
              )}
            </td>
          </tr>

          {/* ===== CHARGES DU PROGRAMME ===== */}
          <tr className="tw-bg-orange-100 tw-border-t-2 tw-border-orange-300">
            <td colSpan={9} className="tw-px-4 tw-py-3 tw-font-bold tw-text-orange-800 tw-text-base">
              Charges du programme
            </td>
          </tr>

          {/* R&eacute;trocession au R&eacute;seau */}
          {isTransferPrice && (
            <tr className="tw-border-b tw-border-gray-100 tw-bg-orange-50/30">
              <td className="tw-px-4 tw-py-2 tw-font-semibold tw-text-orange-700">
                R&eacute;trocession au R&eacute;seau
                <HelpTooltip
                  content="R&eacute;trocession des garanties aux agences, consid&eacute;r&eacute;e comme d&eacute;caiss&eacute;e selon param&eacute;trage d&eacute;fini."
                  id="tooltip-retrocession"
                />
              </td>
              <td className="tw-px-2 tw-py-2 tw-text-right tw-font-semibold">
                {data.retrocession.total.count || 0}
              </td>
              <td className="tw-px-2 tw-py-2 tw-text-right"></td>
              <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium">
                {formatPrice(data.retrocession.total.factureTtc || 0)}
              </td>
              <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500">
                {formatPrice(data.retrocession.total.factureHt || 0)}
              </td>
              <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-bg-emerald-50/30">
                {formatPrice(data.retrocession.total.encaisseTtc || 0)}
              </td>
              <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500 tw-bg-emerald-50/30">
                {formatPrice(data.retrocession.total.encaisseHt || 0)}
              </td>
              <td className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-bg-amber-50/30">
                {formatPrice(data.retrocession.total.aEncaisserTtc || 0)}
              </td>
              <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500 tw-bg-amber-50/30">
                {formatPrice(data.retrocession.total.aEncaisserHt || 0)}
              </td>
            </tr>
          )}

          <tr className="tw-border-b tw-border-gray-100 tw-bg-orange-50/30">
            <td className="tw-px-4 tw-py-2 tw-font-semibold tw-text-orange-700">
              Prise en charge SAV
              <HelpTooltip
                content="SAV pris en charge sur la p&eacute;riode d&eacute;finie"
                id="tooltip-prise-en-charge-sav"
              />
            </td>
            <td className="tw-px-2 tw-py-2 tw-text-right tw-font-semibold">
              {data.priseEnChargeSav.total.count || 0}
            </td>
            <td className="tw-px-2 tw-py-2 tw-text-right"></td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium">
              {formatPrice(data.priseEnChargeSav.total.factureTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500">
              {formatPrice(data.priseEnChargeSav.total.factureHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-bg-emerald-50/30">
              {formatPrice(data.priseEnChargeSav.total.encaisseTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500 tw-bg-emerald-50/30">
              {formatPrice(data.priseEnChargeSav.total.encaisseHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-bg-amber-50/30">
              {formatPrice(data.priseEnChargeSav.total.aEncaisserTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500 tw-bg-amber-50/30">
              {formatPrice(data.priseEnChargeSav.total.aEncaisserHt || 0)}
            </td>
          </tr>
          {/* Sous-ligne: Au titre de la garantie */}
          <tr className="tw-border-b tw-border-gray-100">
            <td className="tw-px-4 tw-py-1.5 tw-pl-10 tw-text-gray-600">
              Au titre de la garantie
              <HelpTooltip
                content="SAV pris en charge au titre du contrat"
                id="tooltip-sav-garantie"
              />
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-600">
              {data.priseEnChargeSav.garantie.count || 0}
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-500"></td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500">
              {formatPrice(data.priseEnChargeSav.garantie.factureTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400">
              {formatPrice(data.priseEnChargeSav.garantie.factureHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500 tw-bg-emerald-50/20">
              {formatPrice(data.priseEnChargeSav.garantie.encaisseTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-emerald-50/20">
              {formatPrice(data.priseEnChargeSav.garantie.encaisseHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-600 tw-bg-amber-50/20">
              {formatPrice(data.priseEnChargeSav.garantie.aEncaisserTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-amber-50/20">
              {formatPrice(data.priseEnChargeSav.garantie.aEncaisserHt || 0)}
            </td>
          </tr>
          {/* Sous-ligne: Geste commercial */}
          <tr className="tw-border-b tw-border-gray-100">
            <td className="tw-px-4 tw-py-1.5 tw-pl-10 tw-text-gray-600">
              Geste commercial
              <HelpTooltip
                content="SAV pris en charge &agrave; titre commercial (hors contrat)"
                id="tooltip-sav-commercial"
              />
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-600">
              {data.priseEnChargeSav.commercial.count || 0}
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-500"></td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500">
              {formatPrice(data.priseEnChargeSav.commercial.factureTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400">
              {formatPrice(data.priseEnChargeSav.commercial.factureHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500 tw-bg-emerald-50/20">
              {formatPrice(data.priseEnChargeSav.commercial.encaisseTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-emerald-50/20">
              {formatPrice(data.priseEnChargeSav.commercial.encaisseHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-600 tw-bg-amber-50/20">
              {formatPrice(data.priseEnChargeSav.commercial.aEncaisserTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-amber-50/20">
              {formatPrice(data.priseEnChargeSav.commercial.aEncaisserHt || 0)}
            </td>
          </tr>

          {hasStripePayments && (<>
          {/* Frais de service */}
          <tr className="tw-border-b tw-border-gray-100 tw-bg-orange-50/30">
            <td className="tw-px-4 tw-py-2 tw-font-semibold tw-text-orange-700">
              Frais de service
              <HelpTooltip
                content="Frais de service pr&eacute;lev&eacute;s sur chaque paiement au comptant ou mensualis&eacute;, commissions rendues par avoir d&eacute;duites"
                id="tooltip-frais-stripe"
              />
            </td>
            <td className="tw-px-2 tw-py-2 tw-text-right tw-font-semibold">
              {data.fraisStripe.total.count || 0}
            </td>
            <td className="tw-px-2 tw-py-2 tw-text-right"></td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium">
              {formatPrice(data.fraisStripe.total.factureTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500">
              {formatPrice(data.fraisStripe.total.factureHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-bg-emerald-50/30">
              {formatPrice(data.fraisStripe.total.encaisseTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500 tw-bg-emerald-50/30">
              {formatPrice(data.fraisStripe.total.encaisseHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-bg-amber-50/30">
              {formatPrice(data.fraisStripe.total.aEncaisserTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500 tw-bg-amber-50/30">
              {formatPrice(data.fraisStripe.total.aEncaisserHt || 0)}
            </td>
          </tr>
          {/* Sous-ligne : Frais support&eacute;s par le b&eacute;n&eacute;ficiaire (mensualis&eacute;) */}
          <tr className="tw-border-b tw-border-gray-100">
            <td className="tw-px-4 tw-py-1.5 tw-pl-10 tw-text-gray-600">
              Support&eacute;s par le b&eacute;n&eacute;ficiaire
              <HelpTooltip
                content="Correspond aux frais sur les contrats mensualis&eacute;s"
                id="tooltip-frais-stripe-client"
              />
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-600">
              {data.fraisStripe.client?.count || 0}
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right"></td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500">
              {formatPrice(data.fraisStripe.client?.factureTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400">
              {formatPrice(data.fraisStripe.client?.factureHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500 tw-bg-emerald-50/20">
              {formatPrice(data.fraisStripe.client?.encaisseTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-emerald-50/20">
              {formatPrice(data.fraisStripe.client?.encaisseHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-600 tw-bg-amber-50/20">
              {formatPrice(data.fraisStripe.client?.aEncaisserTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-amber-50/20">
              {formatPrice(data.fraisStripe.client?.aEncaisserHt || 0)}
            </td>
          </tr>
          {/* Sous-ligne : Frais non support&eacute;s par le b&eacute;n&eacute;ficiaire (comptant Stripe) */}
          <tr className="tw-border-b tw-border-gray-100">
            <td className="tw-px-4 tw-py-1.5 tw-pl-10 tw-text-gray-600">
              Non support&eacute;s par le b&eacute;n&eacute;ficiaire
              <HelpTooltip
                content="Correspond aux frais sur les contrats en paiement comptant"
                id="tooltip-frais-stripe-agence"
              />
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right tw-text-gray-600">
              {data.fraisStripe.agence?.count || 0}
            </td>
            <td className="tw-px-2 tw-py-1.5 tw-text-right"></td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500">
              {formatPrice(data.fraisStripe.agence?.factureTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400">
              {formatPrice(data.fraisStripe.agence?.factureHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-500 tw-bg-emerald-50/20">
              {formatPrice(data.fraisStripe.agence?.encaisseTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-emerald-50/20">
              {formatPrice(data.fraisStripe.agence?.encaisseHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-600 tw-bg-amber-50/20">
              {formatPrice(data.fraisStripe.agence?.aEncaisserTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-1.5 tw-text-right tw-text-gray-400 tw-bg-amber-50/20">
              {formatPrice(data.fraisStripe.agence?.aEncaisserHt || 0)}
            </td>
          </tr>
          </>)}

          {/* Prestation Affinicar */}
          <tr className="tw-border-b tw-border-gray-100 tw-bg-orange-50/30">
            <td className="tw-px-4 tw-py-2 tw-font-semibold tw-text-orange-700">
              Prestation Affinicar
              <HelpTooltip
                content="Prestation Affinicar, consid&eacute;r&eacute;e comme d&eacute;caiss&eacute;e imm&eacute;diatement dans ce tableau (M+1 le 20 en r&eacute;alit&eacute;)"
                id="tooltip-prestation-affinicar"
              />
            </td>
            <td className="tw-px-2 tw-py-2 tw-text-right tw-font-semibold">
              {data.prestationAffinicar.total.count || 0}
            </td>
            <td className="tw-px-2 tw-py-2 tw-text-right"></td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium">
              {formatPrice(data.prestationAffinicar.total.factureTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500">
              {formatPrice(data.prestationAffinicar.total.factureHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-bg-emerald-50/30">
              {formatPrice(data.prestationAffinicar.total.encaisseTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500 tw-bg-emerald-50/30">
              {formatPrice(data.prestationAffinicar.total.encaisseHt || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-semibold tw-bg-amber-50/30">
              {formatPrice(data.prestationAffinicar.total.aEncaisserTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-text-gray-500 tw-bg-amber-50/30">
              {formatPrice(data.prestationAffinicar.total.aEncaisserHt || 0)}
            </td>
          </tr>

          {/* ===== TOTAL CHARGES ===== */}
          <tr className="tw-bg-orange-200 tw-border-t tw-border-orange-400">
            <td className="tw-px-4 tw-py-2 tw-font-bold tw-text-orange-800">
              Total charges
            </td>
            <td className="tw-px-2 tw-py-2 tw-text-right"></td>
            <td className="tw-px-2 tw-py-2 tw-text-right"></td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-bold tw-text-orange-800">
              {formatPrice(
                (effectiveRetrocession.factureTtc || 0) +
                (data.priseEnChargeSav.total.factureTtc || 0) +
                (data.fraisStripe.total.factureTtc || 0) +
                (data.prestationAffinicar.total.factureTtc || 0)
              )}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-text-orange-700">
              {formatPrice(
                (effectiveRetrocession.factureHt || 0) +
                (data.priseEnChargeSav.total.factureHt || 0) +
                (data.fraisStripe.total.factureHt || 0) +
                (data.prestationAffinicar.total.factureHt || 0)
              )}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-bold tw-text-orange-800 tw-bg-emerald-100">
              {formatPrice(
                (effectiveRetrocession.encaisseTtc || 0) +
                (data.priseEnChargeSav.total.encaisseTtc || 0) +
                (data.fraisStripe.total.encaisseTtc || 0) +
                (data.prestationAffinicar.total.encaisseTtc || 0)
              )}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-text-orange-700 tw-bg-emerald-100">
              {formatPrice(
                (effectiveRetrocession.encaisseHt || 0) +
                (data.priseEnChargeSav.total.encaisseHt || 0) +
                (data.fraisStripe.total.encaisseHt || 0) +
                (data.prestationAffinicar.total.encaisseHt || 0)
              )}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-bold tw-text-orange-800 tw-bg-amber-100">
              {formatPrice(
                (effectiveRetrocession.aEncaisserTtc || 0) +
                (data.priseEnChargeSav.total.aEncaisserTtc || 0) +
                (data.fraisStripe.total.aEncaisserTtc || 0) +
                (data.prestationAffinicar.total.aEncaisserTtc || 0)
              )}
            </td>
            <td className="tw-px-3 tw-py-2 tw-text-right tw-font-medium tw-text-orange-700 tw-bg-amber-100">
              {formatPrice(
                (effectiveRetrocession.aEncaisserHt || 0) +
                (data.priseEnChargeSav.total.aEncaisserHt || 0) +
                (data.fraisStripe.total.aEncaisserHt || 0) +
                (data.prestationAffinicar.total.aEncaisserHt || 0)
              )}
            </td>
          </tr>

          {(bilanDisplay.factureTtc || 0) >= 0 && (<>
          {/* ===== BILAN ===== */}
          <tr className="tw-bg-gray-200 tw-border-t-2 tw-border-gray-400">
            <td colSpan={9} className="tw-px-4 tw-py-3 tw-font-bold tw-text-gray-800 tw-text-base">
              Bilan
            </td>
          </tr>
          <tr className="tw-border-b tw-border-gray-200 tw-bg-gray-100">
            <td className="tw-px-4 tw-py-2 tw-text-gray-600 tw-text-xs tw-italic" colSpan={3}>
              Total revenus - Total charges
            </td>
            <td className={`tw-px-3 tw-py-3 tw-text-right tw-font-bold tw-text-lg ${
              (bilanDisplay.factureTtc || 0) >= 0 ? 'tw-text-green-700' : 'tw-text-red-600'
            }`}>
              {formatPrice(bilanDisplay.factureTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-3 tw-text-right tw-font-medium tw-text-gray-600">
              {formatPrice(bilanDisplay.factureHt || 0)}
            </td>
            <td className={`tw-px-3 tw-py-3 tw-text-right tw-font-bold tw-text-lg tw-bg-emerald-100 ${
              (bilanDisplay.encaisseTtc || 0) >= 0 ? 'tw-text-green-700' : 'tw-text-red-600'
            }`}>
              {formatPrice(bilanDisplay.encaisseTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-3 tw-text-right tw-font-medium tw-text-gray-600 tw-bg-emerald-100">
              {formatPrice(bilanDisplay.encaisseHt || 0)}
            </td>
            <td className={`tw-px-3 tw-py-3 tw-text-right tw-font-bold tw-text-lg tw-bg-amber-100 ${
              (bilanDisplay.aEncaisserTtc || 0) >= 0 ? 'tw-text-green-700' : 'tw-text-red-600'
            }`}>
              {formatPrice(bilanDisplay.aEncaisserTtc || 0)}
            </td>
            <td className="tw-px-3 tw-py-3 tw-text-right tw-font-medium tw-text-gray-600 tw-bg-amber-100">
              {formatPrice(bilanDisplay.aEncaisserHt || 0)}
            </td>
          </tr>
          </>)}
        </tbody>
      </table>
    </div>
    </div>
  );
}
